import { ScopeHooks } from "sockapi/scope-specs";
import { getTeamRoundScopeIdComponents } from "sockapi/scope-specs-util";
import { ScopeType } from "sockapi/scope-types";
import { ScopeTeamRoundIdentifier } from "sockapi/scopes";

export enum MovableType {
  BOX = "box",
  PIECE = "piece",
  BOX_PIECE = "boxPiece",
}

export const ROUND_IDS = {
  intro: "intro",
  crosswords: "crosswords",
  atoll: "atoll",
  site: "site",
  desert: "desert",
  endgame: "endgame",
  posthunt: "posthunt",
};

type MovableBase = {
  /** What version of this thing is this? We ignore updates if the version isn't newer. */
  version: number;
  /** What order to place this thing in, if the layout is list/grid. */
  order: number;
  /** x-ratio (0 to 1, distance to left) to place this, if the layout is free. */
  col: number;
  /** y-ratio (0 to 1, distance to top) to place this, if the layout is free. */
  row: number;
  /** Height, as a ratio of size to height of the ambient box. */
  cols: number;
  /** Width, as a ratio of size to height of the ambient box. */
  rows: number;
  /** Rotation in degrees. */
  rot: number;
  /**
   * Is it piece flipped? There's only one piece that can be flipped
   * (convergence-f), and we only added this constraint later during
   * the hunt, so we make it optional because we have to deal with it
   * potentially not being defined...
   */
  flipped?: boolean;
};

/** A piece is a movable thing that belongs to an arbitrary number of boxes. */
export type Piece = MovableBase & {
  type: MovableType.PIECE;
  /** ID for this piece: puzName for the piece. */
  id: string;
};

/** A box is a movable thing that contains some number of pieces. */
export type Box = MovableBase & {
  type: MovableType.BOX;
  /** ID for this box: puzName for this puzzle. */
  id: string;
};

/**
 * A relationship between a box and a piece it could contain. The movable here
 * is the piece, relative to the view inside the box.
 *
 * Note that just because a boxPiece exists doesn't mean that the piece is
 * actually inside the box; that's handled by `active`.
 */
export type BoxPiece = MovableBase & {
  type: MovableType.BOX_PIECE;
  /** ID for this BoxPiece; use makeBoxPieceId, getBoxId, getPieceId. */
  id: string;
  /** Is this *actually* inside the box? */
  active: boolean;
};

export function makeBoxPieceId(boxId: string | null, pieceId: string): string {
  return boxId ? `${boxId}:${pieceId}` : pieceId;
}
export function parseBoxPieceId(boxPieceId: string): {
  boxId: string;
  pieceId: string;
} {
  return { boxId: boxPieceId.split(":")[0], pieceId: boxPieceId.split(":")[1] };
}

export function getBoxId(boxPieceId: string): string;
export function getBoxId(boxPiece: Box | BoxPiece): string;
export function getBoxId(boxPiece: string | Box | BoxPiece): string {
  if (typeof boxPiece === "string") {
    if (boxPiece.includes(":")) return boxPiece.split(":")[0];
    return boxPiece;
  }
  if (boxPiece.type === MovableType.BOX) return boxPiece.id;
  return boxPiece.id.split(":")[0];
}

export function getPieceId(boxPieceId: string): string;
export function getPieceId(boxPiece: Piece | BoxPiece): string;
export function getPieceId(boxPiece: string | Piece | BoxPiece): string {
  if (typeof boxPiece === "string") {
    if (boxPiece.includes(":")) return boxPiece.split(":")[1];
    return boxPiece;
  }
  if (boxPiece.type === MovableType.PIECE) return boxPiece.id;
  return boxPiece.id.split(":")[1];
}

/** A thing that's draggable / movable within a round. */
export type Movable = Box | Piece | BoxPiece;

export enum RoundStepType {
  UPDATE = "update",
}

export type RoundScopeUpdateUnversioned = {
  movables: Record<string, Partial<Movable> & { version?: never }>;
};

export type RoundScopeUpdate = {
  bgImage?: string;
  movables: Record<
    string,
    Partial<Movable> & Required<Pick<Movable, "version">>
  >;
  newMovables?: Record<string, Movable>;
  deleteMovables?: string[];
};

type RoundScopeBackendState = {
  movables: Record<string, Movable>;
};

export type RoundScopeSpec = {
  identifier: ScopeTeamRoundIdentifier;
  state: {
    bgImage: string;
  } & RoundScopeBackendState;
  update: RoundScopeUpdate;
  /** An update is a transaction---either it's all applied or it's all rejected. */
  step: { type: RoundStepType.UPDATE } & Pick<RoundScopeUpdate, "movables">;
  stepResponse: { success: boolean };
  // Remove this if the scope doesn't initialize from backend state.
  backendState: RoundScopeBackendState;
};

export const roundScopeHooks: ScopeHooks<ScopeType.ROUND> = {
  identityFunc: (scope) => scope,
  getIdComponents: getTeamRoundScopeIdComponents,
  update: (scopeState, payload) => {
    for (const [movableId, movable] of Object.entries(
      payload.newMovables ?? {}
    )) {
      scopeState.movables[movableId] = movable;
      scopeState.movables[movableId].order += 1000;
    }
    for (const movableId of payload.deleteMovables ?? []) {
      delete scopeState.movables[movableId];
    }

    for (const [movableId, movable] of Object.entries(payload.movables ?? {})) {
      if (!scopeState.movables[movableId]) {
        if (!movableId.includes(":")) {
          // Only allow making new boxPieces.
          continue;
        }
        scopeState.movables[movableId] = {
          type: MovableType.BOX_PIECE,
          id: movableId,
          active: true,
          version: 1,
          order: 0,
          col: 0,
          row: 0,
          cols: 0,
          rows: 0,
          rot: 0,
        } satisfies BoxPiece;
      }
      scopeState.movables[movableId] = Object.assign(
        scopeState.movables[movableId],
        movable
      );
      scopeState.movables[movableId].order += 2000;
    }

    // Renumber movable orders to be sequential from 1 within each layer, with
    // the latest changes at the end. These translate directly into z-indices.
    const movableLayers: Record<string, Movable[]> = {
      [MovableType.BOX]: [],
      [MovableType.PIECE]: [],
    };
    for (const movable of Object.values(scopeState.movables)) {
      const layer =
        movable.type === MovableType.BOX_PIECE
          ? getBoxId(movable)
          : movable.type;
      (movableLayers[layer] ??= []).push(movable);
    }
    for (const movables of Object.values(movableLayers)) {
      movables
        .sort((a, b) => a.order - b.order)
        .forEach((movable, i) => {
          movable.order = i + 1;
        });
    }

    if (payload.bgImage !== undefined) {
      scopeState.bgImage = payload.bgImage;
    }
  },
};
